import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Input,
  Spinner,
  Tab,
  TabList,
  Textarea,
} from "@fluentui/react-components";
import {
  Alert20Regular,
  Book20Regular,
  Building20Regular,
  Chat20Regular,
  Checkmark20Regular,
  Delete20Regular,
  Keyboard20Regular,
  Mail20Regular,
  Person20Regular,
  QuestionCircle20Regular,
  Search20Regular,
  Settings20Regular,
  Shield20Regular,
  SignOut20Regular,
} from "@fluentui/react-icons";
import useAuth from "../../hooks/useAuth";
import { apiCall } from "../../api/services";
import { useNotifications } from "./Notificationsstore";
import styles from "./Headerpages.module.css";

/* ------------------------------------------------------------------ */
/* Pages and the paths the header menus already use                     */
/* ------------------------------------------------------------------ */

export type HeaderPageKey =
  | "notifications"
  | "settings"
  | "profile"
  | "company"
  | "help"
  | "guides"
  | "shortcuts"
  | "contact";

/** Header menu path -> page. "/account" is the same page as "My account". */
export const HEADER_PAGE_PATHS: Record<string, HeaderPageKey> = {
  "/notifications": "notifications",
  "/settings": "settings",
  "/settings/profile": "profile",
  "/account": "profile",
  "/settings/company": "company",
  "/help": "help",
  "/help/guides": "guides",
  "/help/shortcuts": "shortcuts",
  "/contact": "contact",
};

const SUPPORT_EMAIL = "support@cilpron.com";

interface NavEntry {
  key: HeaderPageKey;
  label: string;
  icon: React.ReactElement;
}

const ACCOUNT_NAV: NavEntry[] = [
  { key: "profile", label: "My account", icon: <Person20Regular /> },
  { key: "company", label: "Company", icon: <Building20Regular /> },
  { key: "notifications", label: "Notifications", icon: <Alert20Regular /> },
  { key: "settings", label: "All settings", icon: <Settings20Regular /> },
];

const HELP_NAV: NavEntry[] = [
  { key: "help", label: "Help center", icon: <QuestionCircle20Regular /> },
  { key: "guides", label: "User guides", icon: <Book20Regular /> },
  { key: "shortcuts", label: "Keyboard shortcuts", icon: <Keyboard20Regular /> },
  { key: "contact", label: "Contact support", icon: <Chat20Regular /> },
];

const TITLES: Record<HeaderPageKey, { title: string; sub: string }> = {
  notifications: { title: "Notifications", sub: "Everything that needs your attention, in one place." },
  settings: { title: "All settings", sub: "Your account, your company and how the apps are set up." },
  profile: { title: "My account", sub: "Your sign-in details and what you can do in each app." },
  company: { title: "Company", sub: "Your organisation and the apps it subscribes to." },
  help: { title: "Help center", sub: "Answers to common questions and guides for every app." },
  guides: { title: "User guides", sub: "Step-by-step guides for setting up and using each app." },
  shortcuts: { title: "Keyboard shortcuts", sub: "Get around faster without leaving the keyboard." },
  contact: { title: "Contact support", sub: "Tell us what you need and we will get back to you." },
};

/* ------------------------------------------------------------------ */
/* Small helpers                                                        */
/* ------------------------------------------------------------------ */

const asList = <T,>(v: any): T[] =>
  Array.isArray(v) ? v : Array.isArray(v?.$values) ? v.$values : [];

const authCall = async (token: string, endpoint: string): Promise<any> => {
  const res = await apiCall({
    service: "auth",
    endpoint,
    method: "GET",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  } as any);
  if (res && (res as any).error) {
    const e = (res as any).error;
    throw new Error(typeof e === "string" ? e : "The request failed");
  }
  return res;
};

const Empty: React.FC<{ icon: React.ReactElement; title: string; text?: string }> = ({
  icon,
  title,
  text,
}) => (
  <div className={styles.hpEmpty}>
    <span className={styles.hpEmptyIcon}>{icon}</span>
    <strong>{title}</strong>
    {text && <span>{text}</span>}
  </div>
);

/* ------------------------------------------------------------------ */
/* Notifications                                                        */
/* ------------------------------------------------------------------ */

const NotificationsPage: React.FC = () => {
  const { notes, markRead, markUnread, markAllRead, remove, clear } = useNotifications();
  const [tab, setTab] = useState<"all" | "unread">("all");
  const unread = notes.filter((n) => !n.read).length;
  const shown = tab === "unread" ? notes.filter((n) => !n.read) : notes;

  return (
    <div className={styles.hpCard}>
      <div className={styles.hpToolbar}>
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(d.value as "all" | "unread")}>
          <Tab value="all">All ({notes.length})</Tab>
          <Tab value="unread">Unread ({unread})</Tab>
        </TabList>
        <div className={styles.hpToolbarEnd}>
          <Button size="small" icon={<Checkmark20Regular />} disabled={!unread} onClick={markAllRead}>
            Mark all read
          </Button>
          <Button size="small" icon={<Delete20Regular />} disabled={!notes.length} onClick={clear}>
            Clear all
          </Button>
        </div>
      </div>
      {shown.length === 0 ? (
        <Empty
          icon={<Alert20Regular />}
          title={tab === "unread" ? "No unread notifications" : "You're all caught up"}
          text="New activity from your apps will show up here."
        />
      ) : (
        <ul className={styles.hpList}>
          {shown.map((n) => (
            <li key={n.id} className={`${styles.hpNote} ${n.read ? "" : styles.hpNoteNew}`}>
              <span className={styles.hpDot} />
              <div className={styles.hpNoteText}>
                <span className={styles.hpNoteTitle}>{n.title}</span>
                <span className={styles.hpNoteBody}>{n.body}</span>
                <span className={styles.hpNoteTime}>{n.time}</span>
              </div>
              <div className={styles.hpNoteActions}>
                <Button
                  size="small"
                  appearance="subtle"
                  onClick={() => (n.read ? markUnread(n.id) : markRead(n.id))}
                >
                  {n.read ? "Mark unread" : "Mark read"}
                </Button>
                <Button
                  size="small"
                  appearance="subtle"
                  aria-label="Delete notification"
                  icon={<Delete20Regular />}
                  onClick={() => remove(n.id)}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* My account                                                           */
/* ------------------------------------------------------------------ */

const APP_KEYS: { key: string; label: string }[] = [
  { key: "hms", label: "Hospitality" },
  { key: "crm", label: "CRM" },
];

const ProfilePage: React.FC<{ onSignOut: () => void }> = ({ onSignOut }) => {
  const { user } = useAuth() as any;
  const fullName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || "User";
  const [appKey, setAppKey] = useState("hms");
  const [custom, setCustom] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [roles, setRoles] = useState<string[]>([]);
  const [perms, setPerms] = useState<string[]>([]);
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState("");

  const effectiveKey = (custom.trim() || appKey).toLowerCase();

  const load = async () => {
    if (!user?.token) return;
    setState("loading");
    setErr("");
    try {
      const me = await authCall(user.token, `me/permissions?app=${encodeURIComponent(effectiveKey)}`);
      setRoles(asList<string>(me?.roles));
      setPerms(asList<string>(me?.permissions));
      setState("done");
    } catch (e: any) {
      setErr(e?.message || "Could not load your access.");
      setState("error");
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token, appKey]);

  const shownPerms = perms.filter((p) => p.toLowerCase().includes(filter.trim().toLowerCase()));

  const rows: [string, string][] = [
    ["First name", user?.firstName ?? "—"],
    ["Last name", user?.lastName ?? "—"],
    ["Email", user?.email ?? "—"],
    ["Phone", user?.phone ?? "—"],
  ];

  return (
    <>
      <div className={styles.hpCard}>
        <div className={styles.hpProfileHead}>
          <Avatar name={fullName} size={72} />
          <div>
            <h3 className={styles.hpName}>{fullName}</h3>
            <span className={styles.hpMuted}>{user?.email ?? ""}</span>
          </div>
          <div className={styles.hpToolbarEnd}>
            <Button icon={<SignOut20Regular />} onClick={onSignOut}>
              Sign out
            </Button>
          </div>
        </div>
        <dl className={styles.hpFacts}>
          {rows.map(([k, v]) => (
            <div key={k} className={styles.hpFact}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
        <p className={styles.hpHint}>
          These details come from your sign-in. To change them, ask your administrator.
        </p>
      </div>

      <div className={styles.hpCard}>
        <div className={styles.hpCardHead}>
          <div>
            <h4 className={styles.hpCardTitle}>My access</h4>
            <span className={styles.hpMuted}>The roles and permissions you hold in an app.</span>
          </div>
        </div>
        <div className={styles.hpToolbar}>
          <TabList
            selectedValue={custom.trim() ? "" : appKey}
            onTabSelect={(_, d) => {
              setCustom("");
              setAppKey(String(d.value));
            }}
          >
            {APP_KEYS.map((a) => (
              <Tab key={a.key} value={a.key}>
                {a.label}
              </Tab>
            ))}
          </TabList>
          <div className={styles.hpToolbarEnd}>
            <Input
              size="small"
              placeholder="Other app key"
              value={custom}
              onChange={(_, d) => setCustom(d.value)}
            />
            <Button size="small" disabled={!custom.trim()} onClick={() => void load()}>
              Check
            </Button>
          </div>
        </div>
        {state === "loading" && (
          <div className={styles.hpCenter}>
            <Spinner size="small" label="Loading your access…" />
          </div>
        )}
        {state === "error" && (
          <Empty icon={<Shield20Regular />} title="Could not load your access" text={err} />
        )}
        {state === "done" && (
          <div className={styles.hpAccess}>
            <div>
              <div className={styles.hpLabel}>Roles</div>
              {roles.length === 0 ? (
                <span className={styles.hpMuted}>No roles in this app.</span>
              ) : (
                <div className={styles.hpChips}>
                  {roles.map((r) => (
                    <span key={r} className={styles.hpChip}>
                      {r}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className={styles.hpLabelRow}>
                <span className={styles.hpLabel}>Permissions ({perms.length})</span>
                <Input
                  size="small"
                  contentBefore={<Search20Regular />}
                  placeholder="Filter"
                  value={filter}
                  onChange={(_, d) => setFilter(d.value)}
                />
              </div>
              {shownPerms.length === 0 ? (
                <span className={styles.hpMuted}>
                  {perms.length ? "No permission matches your filter." : "No permissions in this app."}
                </span>
              ) : (
                <div className={styles.hpChips}>
                  {shownPerms.map((p) => (
                    <span key={p} className={`${styles.hpChip} ${styles.hpChipSoft}`}>
                      {p}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Company                                                              */
/* ------------------------------------------------------------------ */

const CompanyPage: React.FC = () => {
  const { user } = useAuth() as any;
  const modules = asList<{ moduleName?: string; isActive?: boolean }>(user?.subscription?.modules);
  const active = modules.filter((m) => m.isActive).length;
  const [tenant, setTenant] = useState<string>("");

  useEffect(() => {
    if (!user?.token) return;
    let live = true;
    (async () => {
      try {
        const me = await authCall(user.token, "me/permissions?app=hms");
        if (live && me?.tenantId) setTenant(String(me.tenantId));
      } catch {
        /* the company ID is optional; the page works without it */
      }
    })();
    return () => {
      live = false;
    };
  }, [user?.token]);

  return (
    <>
      <div className={styles.hpTiles}>
        <div className={styles.hpTile}>
          <span className={styles.hpTileLabel}>Apps subscribed</span>
          <span className={styles.hpTileValue}>{modules.length}</span>
        </div>
        <div className={styles.hpTile}>
          <span className={styles.hpTileLabel}>Active</span>
          <span className={styles.hpTileValue}>{active}</span>
        </div>
        <div className={styles.hpTile}>
          <span className={styles.hpTileLabel}>Inactive</span>
          <span className={styles.hpTileValue}>{modules.length - active}</span>
        </div>
      </div>

      <div className={styles.hpCard}>
        <div className={styles.hpCardHead}>
          <h4 className={styles.hpCardTitle}>Subscription</h4>
        </div>
        {modules.length === 0 ? (
          <Empty
            icon={<Building20Regular />}
            title="No subscription details"
            text="Your sign-in did not include any subscribed apps."
          />
        ) : (
          <table className={styles.hpTable}>
            <thead>
              <tr>
                <th>App</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((m, i) => (
                <tr key={`${m.moduleName}-${i}`}>
                  <td>{m.moduleName}</td>
                  <td>
                    <span className={m.isActive ? styles.hpPillOn : styles.hpPillOff}>
                      {m.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.hpCard}>
        <div className={styles.hpCardHead}>
          <h4 className={styles.hpCardTitle}>Organisation</h4>
        </div>
        <dl className={styles.hpFacts}>
          <div className={styles.hpFact}>
            <dt>Company ID</dt>
            <dd className={styles.hpMono}>{tenant || "—"}</dd>
          </div>
          <div className={styles.hpFact}>
            <dt>Billing and plan changes</dt>
            <dd>
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </dd>
          </div>
        </dl>
      </div>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Help content                                                         */
/* ------------------------------------------------------------------ */

export interface Guide {
  id: string;
  title: string;
  summary: string;
  steps: string[];
}

export const GUIDES: Guide[] = [
  {
    id: "start",
    title: "Getting started",
    summary: "Find your way around the apps.",
    steps: [
      "Select the grid icon at the top left to see and open all your apps.",
      "Use the Quick Link box in the middle of the top bar to jump to a question or answer quickly.",
      "The bell shows your notifications, the question icon opens help, and the cog opens settings.",
      "Select your picture at the top right to see your account, settings and to sign out.",
    ],
  },
  {
    id: "property",
    title: "Set up a property and its rooms",
    summary: "Add the property, then its room types, categories, amenities and rooms.",
    steps: [
      "Open Settings, then Property, then Add Property, and save your property.",
      "Under Room Setups, add Room Types, Room Category and Amenity first.",
      "Then choose Add Rooms and pick the type and category you just created.",
    ],
  },
  {
    id: "access",
    title: "Give people the right access",
    summary: "Create roles, choose permissions and assign them.",
    steps: [
      "Open Settings, then Access Control, then Roles, and add a role.",
      "Under Permissions, review what can be granted.",
      "Under Assign Permissions, choose a role and tick the permissions it should have, then save.",
      "People get access through the roles they hold. My account shows the roles and permissions you hold.",
    ],
  },
  {
    id: "accounting",
    title: "Set up Accounting",
    summary: "The accounts, banks, taxes and contacts the books depend on.",
    steps: [
      "Open Settings, then Accounting, then Accounting Setup.",
      "Start with Account groups and Chart of accounts.",
      "Add your Bank accounts and Tax rates.",
      "Finish with Customers, Vendors and Asset categories.",
    ],
  },
  {
    id: "crm",
    title: "Set up CRM",
    summary: "Organisation, sales, products and service settings.",
    steps: [
      "Open Settings and scroll to CRM.",
      "Pick a group (Organization, Sales setup, Products & pricing, Service & support and so on).",
      "Choose a screen to see its records, then use Add to create one or the row actions to edit it.",
    ],
  },
  {
    id: "retail",
    title: "Set up Retail & POS",
    summary: "Stores come first, then registers and everything that sells.",
    steps: [
      "Open Settings and scroll to Retail & POS.",
      "Add a store under Stores & devices.",
      "Add a register for that store. The till screen needs at least one register before it can open.",
      "Then set up tenders, tax, categories and suppliers.",
    ],
  },
  {
    id: "fo",
    title: "Set up Finance & Operations",
    summary: "Legal entities, warehouses, partners, items and workers.",
    steps: [
      "Open Settings and scroll to Finance & Operations.",
      "Add your Legal entities and Operating units under Organization.",
      "Add Warehouses, Business partners and Items under Master data.",
      "Add Workers, and review Number sequences under People & system.",
    ],
  },
];

export interface Faq {
  q: string;
  a: string;
  topic: string;
}

export const FAQS: Faq[] = [
  {
    topic: "Account",
    q: "How do I sign out?",
    a: "Select your picture at the top right and choose Sign out.",
  },
  {
    topic: "Account",
    q: "How do I change my name, email or phone number?",
    a: "These come from your sign-in and are shown on My account. Ask your administrator to change them.",
  },
  {
    topic: "Apps",
    q: "Why can't I open an app?",
    a: "Your company may not be subscribed to it. Company shows which apps are active. Contact your administrator or support to add one.",
  },
  {
    topic: "Apps",
    q: "The till says there are no registers.",
    a: "Add a store and then a register under Settings, Retail & POS, Stores & devices. The till opens once a register exists.",
  },
  {
    topic: "Access",
    q: "Why can't I save changes in Settings?",
    a: "Your role may not include permission for that action. My account shows the roles and permissions you hold in each app. Ask an administrator to update your role.",
  },
  {
    topic: "Access",
    q: "How do I give someone more access?",
    a: "Open Settings, Access Control, Assign Permissions. Choose their role and tick the permissions it should have.",
  },
  {
    topic: "Notifications",
    q: "How do I clear my notifications?",
    a: "Open the bell and choose View all. You can mark items read, delete them one by one or clear everything.",
  },
];

const includes = (hay: string, needle: string) => hay.toLowerCase().includes(needle.toLowerCase());

const GuideCard: React.FC<{ g: Guide; open: boolean; onToggle: () => void; onOpenSetup: () => void }> = ({
  g,
  open,
  onToggle,
  onOpenSetup,
}) => (
  <div className={styles.hpGuide}>
    <button type="button" className={styles.hpGuideHead} onClick={onToggle} aria-expanded={open}>
      <span className={styles.hpGuideIcon}>
        <Book20Regular />
      </span>
      <span className={styles.hpGuideText}>
        <span className={styles.hpGuideTitle}>{g.title}</span>
        <span className={styles.hpMuted}>{g.summary}</span>
      </span>
      <span className={styles.hpChevron}>{open ? "−" : "+"}</span>
    </button>
    {open && (
      <div className={styles.hpGuideBody}>
        <ol className={styles.hpSteps}>
          {g.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <Button size="small" appearance="primary" onClick={onOpenSetup}>
          Open Settings
        </Button>
      </div>
    )}
  </div>
);

const FaqItem: React.FC<{ f: Faq }> = ({ f }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.hpFaq}>
      <button type="button" className={styles.hpFaqQ} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>{f.q}</span>
        <span className={styles.hpChevron}>{open ? "−" : "+"}</span>
      </button>
      {open && <div className={styles.hpFaqA}>{f.a}</div>}
    </div>
  );
};

const HelpPage: React.FC<{ go: (p: HeaderPageKey) => void; onOpenSetup: () => void }> = ({ go, onOpenSetup }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const faqs = useMemo(() => FAQS.filter((f) => !q.trim() || includes(f.q + f.a, q)), [q]);
  const guides = useMemo(
    () => GUIDES.filter((g) => !q.trim() || includes(g.title + g.summary + g.steps.join(" "), q)),
    [q]
  );

  return (
    <>
      <div className={styles.hpHero}>
        <h3 className={styles.hpHeroTitle}>How can we help?</h3>
        <Input
          className={styles.hpHeroSearch}
          size="large"
          contentBefore={<Search20Regular />}
          placeholder="Search guides and questions"
          value={q}
          onChange={(_, d) => setQ(d.value)}
        />
      </div>

      {!q.trim() && (
        <div className={styles.hpQuick}>
          <button type="button" className={styles.hpQuickCard} onClick={() => go("guides")}>
            <Book20Regular />
            <strong>User guides</strong>
            <span>Step-by-step setup for every app</span>
          </button>
          <button type="button" className={styles.hpQuickCard} onClick={() => go("shortcuts")}>
            <Keyboard20Regular />
            <strong>Keyboard shortcuts</strong>
            <span>Move faster with the keyboard</span>
          </button>
          <button type="button" className={styles.hpQuickCard} onClick={() => go("contact")}>
            <Chat20Regular />
            <strong>Contact support</strong>
            <span>Ask us directly</span>
          </button>
        </div>
      )}

      {guides.length > 0 && (
        <div className={styles.hpCard}>
          <div className={styles.hpCardHead}>
            <h4 className={styles.hpCardTitle}>Guides</h4>
          </div>
          <div className={styles.hpGuides}>
            {guides.map((g) => (
              <GuideCard
                key={g.id}
                g={g}
                open={open === g.id}
                onToggle={() => setOpen(open === g.id ? null : g.id)}
                onOpenSetup={onOpenSetup}
              />
            ))}
          </div>
        </div>
      )}

      <div className={styles.hpCard}>
        <div className={styles.hpCardHead}>
          <h4 className={styles.hpCardTitle}>Common questions</h4>
        </div>
        {faqs.length === 0 ? (
          <Empty
            icon={<Search20Regular />}
            title="Nothing matches your search"
            text="Try different words, or contact support."
          />
        ) : (
          faqs.map((f) => <FaqItem key={f.q} f={f} />)
        )}
      </div>
    </>
  );
};

const GuidesPage: React.FC<{ onOpenSetup: () => void }> = ({ onOpenSetup }) => {
  const [open, setOpen] = useState<string | null>(GUIDES[0].id);
  return (
    <div className={styles.hpCard}>
      <div className={styles.hpGuides}>
        {GUIDES.map((g) => (
          <GuideCard
            key={g.id}
            g={g}
            open={open === g.id}
            onToggle={() => setOpen(open === g.id ? null : g.id)}
            onOpenSetup={onOpenSetup}
          />
        ))}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Shortcuts (only the ones the app really has)                         */
/* ------------------------------------------------------------------ */

const SHORTCUTS: { group: string; items: { keys: string[]; what: string }[] }[] = [
  {
    group: "Quick Link search",
    items: [
      { keys: ["↓"], what: "Move to the next suggestion" },
      { keys: ["↑"], what: "Move to the previous suggestion" },
      { keys: ["Tab"], what: "Complete the highlighted suggestion without searching" },
      { keys: ["Enter"], what: "Run the highlighted suggestion, or search for what you typed" },
      { keys: ["Esc"], what: "Close the suggestions" },
    ],
  },
];

const ShortcutsPage: React.FC = () => (
  <>
    {SHORTCUTS.map((g) => (
      <div key={g.group} className={styles.hpCard}>
        <div className={styles.hpCardHead}>
          <h4 className={styles.hpCardTitle}>{g.group}</h4>
        </div>
        <table className={styles.hpTable}>
          <tbody>
            {g.items.map((s) => (
              <tr key={s.what}>
                <td className={styles.hpKeysCell}>
                  {s.keys.map((k) => (
                    <kbd key={k} className={styles.hpKbd}>
                      {k}
                    </kbd>
                  ))}
                </td>
                <td>{s.what}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ))}
    <p className={styles.hpHint}>
      Click the Quick Link box first. More shortcuts will appear here as they are added to the apps.
    </p>
  </>
);

/* ------------------------------------------------------------------ */
/* Contact support                                                      */
/* ------------------------------------------------------------------ */

const TOPICS = ["General question", "Something is not working", "Billing or subscription", "Access or permissions", "Feature request"];

const ContactPage: React.FC = () => {
  const { user } = useAuth() as any;
  const [topic, setTopic] = useState(TOPICS[0]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const fullName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();

  const send = () => {
    const body = `${message.trim()}\n\n—\n${fullName}${user?.email ? `\n${user.email}` : ""}${
      user?.phone ? `\n${user.phone}` : ""
    }`;
    const subj = `[${topic}] ${subject.trim()}`;
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`;
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard can be blocked; the address is also shown in plain text */
    }
  };

  return (
    <div className={styles.hpTwo}>
      <div className={styles.hpCard}>
        <div className={styles.hpCardHead}>
          <h4 className={styles.hpCardTitle}>Send a message</h4>
        </div>
        <div className={styles.hpForm}>
          <label className={styles.hpField}>
            <span className={styles.hpLabel}>What is it about?</span>
            <select className={styles.hpSelect} value={topic} onChange={(e) => setTopic(e.target.value)}>
              {TOPICS.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </label>
          <label className={styles.hpField}>
            <span className={styles.hpLabel}>Subject</span>
            <Input value={subject} onChange={(_, d) => setSubject(d.value)} placeholder="A short summary" />
          </label>
          <label className={styles.hpField}>
            <span className={styles.hpLabel}>Message</span>
            <Textarea
              rows={7}
              resize="vertical"
              value={message}
              onChange={(_, d) => setMessage(d.value)}
              placeholder="Tell us what happened and what you expected."
            />
          </label>
          <div>
            <Button
              appearance="primary"
              icon={<Mail20Regular />}
              disabled={!subject.trim() || !message.trim()}
              onClick={send}
            >
              Send message
            </Button>
          </div>
          <p className={styles.hpHint}>
            This opens your email app with the message ready to send, including your name and email so we can reply.
          </p>
        </div>
      </div>
      <div className={styles.hpCard}>
        <div className={styles.hpCardHead}>
          <h4 className={styles.hpCardTitle}>Email us directly</h4>
        </div>
        <div className={styles.hpForm}>
          <a className={styles.hpMail} href={`mailto:${SUPPORT_EMAIL}`}>
            <Mail20Regular /> {SUPPORT_EMAIL}
          </a>
          <div>
            <Button size="small" onClick={() => void copy()}>
              {copied ? "Copied" : "Copy address"}
            </Button>
          </div>
          <p className={styles.hpHint}>Include the app you were using and what you clicked, so we can help faster.</p>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* All settings                                                         */
/* ------------------------------------------------------------------ */

const SettingsPage: React.FC<{ go: (p: HeaderPageKey) => void; onOpenSetup: () => void }> = ({ go, onOpenSetup }) => {
  const unread = useNotifications((s) => s.notes.filter((n) => !n.read).length);
  const cards: { title: string; text: string; icon: React.ReactElement; run: () => void; meta?: string }[] = [
    { title: "My account", text: "Your details, and the roles and permissions you hold.", icon: <Person20Regular />, run: () => go("profile") },
    { title: "Company", text: "Your organisation and the apps it subscribes to.", icon: <Building20Regular />, run: () => go("company") },
    {
      title: "Application setup",
      text: "Properties, accounting, CRM, retail, finance and access control.",
      icon: <Settings20Regular />,
      run: onOpenSetup,
    },
    {
      title: "Notifications",
      text: "Read, organise and clear what needs your attention.",
      icon: <Alert20Regular />,
      run: () => go("notifications"),
      meta: unread ? `${unread} unread` : undefined,
    },
    { title: "Help center", text: "Guides and answers for every app.", icon: <QuestionCircle20Regular />, run: () => go("help") },
    { title: "Contact support", text: "Ask the Cilpron team for help.", icon: <Chat20Regular />, run: () => go("contact") },
  ];
  return (
    <div className={styles.hpGrid}>
      {cards.map((c) => (
        <button key={c.title} type="button" className={styles.hpSetCard} onClick={c.run}>
          <span className={styles.hpSetIcon}>{c.icon}</span>
          <span className={styles.hpSetTitle}>{c.title}</span>
          <span className={styles.hpMuted}>{c.text}</span>
          {c.meta && <span className={styles.hpSetMeta}>{c.meta}</span>}
        </button>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Shell                                                                */
/* ------------------------------------------------------------------ */

interface HeaderPagesProps {
  page: HeaderPageKey;
  /** Switch to another header page. */
  onNavigate: (page: HeaderPageKey) => void;
  /** Open the main Settings (Setup) screen. */
  onOpenSetup: () => void;
  onSignOut: () => void;
}

const HeaderPages: React.FC<HeaderPagesProps> = ({ page, onNavigate, onOpenSetup, onSignOut }) => {
  const unread = useNotifications((s) => s.notes.filter((n) => !n.read).length);
  const head = TITLES[page];

  const renderNav = (title: string, items: NavEntry[]) => (
    <div className={styles.hpNavGroup}>
      <div className={styles.hpNavTitle}>{title}</div>
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          className={`${styles.hpNavItem} ${page === it.key ? styles.hpNavOn : ""}`}
          onClick={() => onNavigate(it.key)}
          aria-current={page === it.key ? "page" : undefined}
        >
          {it.icon}
          <span>{it.label}</span>
          {it.key === "notifications" && unread > 0 && <span className={styles.hpNavBadge}>{unread}</span>}
        </button>
      ))}
    </div>
  );

  return (
    <div className={styles.hpRoot}>
      <aside className={styles.hpNav}>
        {renderNav("Account", ACCOUNT_NAV)}
        {renderNav("Help", HELP_NAV)}
      </aside>
      <main className={styles.hpMain}>
        <div className={styles.hpPage}>
          <header className={styles.hpPageHead}>
            <h2 className={styles.hpPageTitle}>{head.title}</h2>
            <p className={styles.hpPageSub}>{head.sub}</p>
          </header>
          {page === "notifications" && <NotificationsPage />}
          {page === "settings" && <SettingsPage go={onNavigate} onOpenSetup={onOpenSetup} />}
          {page === "profile" && <ProfilePage onSignOut={onSignOut} />}
          {page === "company" && <CompanyPage />}
          {page === "help" && <HelpPage go={onNavigate} onOpenSetup={onOpenSetup} />}
          {page === "guides" && <GuidesPage onOpenSetup={onOpenSetup} />}
          {page === "shortcuts" && <ShortcutsPage />}
          {page === "contact" && <ContactPage />}
        </div>
      </main>
    </div>
  );
};

export default HeaderPages;